import * as d3 from 'd3';

import { applicationThemeHex } from './applicationThemeHex';

export type VariantProp =
  | 'alternative'
  | 'attention'
  | 'brand'
  | 'negative'
  | 'neutral'
  | 'positive';

const checkered = `url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 4 4' fill='none' opacity='0.02' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='2' height='2' fill='black'/%3E%3Crect x='2' width='2' height='2' fill='white'/%3E%3Crect y='2' width='2' height='2' fill='white'/%3E%3Crect x='2' y='2' width='2' height='2' fill='black'/%3E%3C/svg%3E")`;

const base = {
  transparent: 'transparent',
  white: applicationThemeHex.light,
  black: '#000000',
  amber50: '#FFEFD6',
  amber100: '#FFD89B',
  amber200: '#FFC86B',
  amber300: '#FFB84D',
  amber400: '#F0A034',
  amber500: '#D88820',
  amber600: '#BC700A',
  amber700: '#A05F08',
  amber800: '#8B4F07',
  amber900: '#6B4006',
  blue50: '#DDE9FF',
  blue100: '#ADC8FF',
  blue200: '#7BA7FF',
  blue300: '#5A93FF',
  blue400: '#4782F5',
  blue500: '#3871E0',
  blue600: '#2B62CB',
  blue700: '#2456B5',
  blue800: '#1E4A9E',
  blue900: '#1A3E87',
  brand50: '#E8EAFF',
  brand100: '#D1D7FF',
  brand200: '#B5C0FF',
  brand300: '#9AABFF',
  brand400: '#7F96FF',
  brand500: '#6A82E6',
  brand600: '#5461C8',
  brand700: '#4A52B5',
  brand800: '#3E46A0',
  brand900: '#323A8A',
  gray50: '#F5F6FA',
  gray75: '#F9FAFC',
  gray100: '#DFE1EB',
  gray200: '#C0C3D4',
  gray300: '#A0A3B8',
  gray400: '#82859A',
  gray500: '#6A6D80',
  gray600: '#545768',
  gray700: '#3E4152',
  gray750: '#333648',
  gray800: applicationThemeHex.dark,
  gray900: '#1F2133',
  green50: '#D4F2DC',
  green100: '#A8E4B8',
  green200: '#7AD894',
  green300: '#54CC72',
  green400: '#3CC259',
  green500: '#24B840',
  green600: '#1D9F34',
  green700: '#19882D',
  green800: '#157524',
  green900: '#115A1E',
  red50: '#FFE6E6',
  red100: '#FFB3B3',
  red200: '#FF8080',
  red300: '#FF4D4D',
  red400: '#E63939',
  red500: '#CC2626',
  red600: '#B31A1A',
  red700: '#A01515',
  red800: '#8A1A1A',
  red900: '#661111',
  checkered,
};

const bg = {
  // Use-case variants
  bgApplicationLight: base.white,
  bgApplicationDark: base.gray800,
  bgSurfaceLight: base.gray75,
  bgSurfaceDark: base.gray750,

  // Standard variants
  bgAlternativeLight: base.blue50,
  bgAlternativeDark: base.blue900,
  bgAttentionLight: base.amber50,
  bgAttentionDark: base.amber800,
  bgBrandLight: base.brand50,
  bgBrandDark: base.brand900,
  bgNegativeLight: base.red50,
  bgNegativeDark: base.red900,
  bgNeutralLight: base.gray50,
  bgNeutralDark: base.gray700,
  bgPositiveLight: base.green50,
  bgPositiveDark: base.green900,
};

const stroke = {
  strokeApplicationLight: base.gray200,
  strokeApplicationDark: base.gray700,
  strokeAlternativeLight: base.blue100,
  strokeAlternativeDark: base.blue800,
  strokeAttentionLight: base.amber100,
  strokeAttentionDark: base.amber700,
  strokeBrandLight: base.brand100,
  strokeBrandDark: base.brand800,
  strokeNegativeLight: base.red100,
  strokeNegativeDark: base.red800,
  strokeNeutralLight: base.gray100,
  strokeNeutralDark: base.gray600,
  strokePositiveLight: base.green300,
  strokePositiveDark: base.green800,
};

const heading = {
  headingAlternativeLight: base.blue800,
  headingAlternativeDark: base.blue100,
  headingAttentionLight: base.amber800,
  headingAttentionDark: base.amber100,
  headingBrandLight: base.brand800,
  headingBrandDark: base.brand100,
  headingNegativeLight: base.red800,
  headingNegativeDark: base.red100,
  headingNeutralLight: base.gray800,
  headingNeutralDark: base.gray50,
  headingPositiveLight: base.green900,
  headingPositiveDark: base.green100,
};

const body = {
  bodyAlternativeLight: base.blue700,
  bodyAlternativeDark: base.blue200,
  bodyAttentionLight: base.amber700,
  bodyAttentionDark: base.amber200,
  bodyBrandLight: base.brand700,
  bodyBrandDark: base.brand200,
  bodyNegativeLight: base.red700,
  bodyNegativeDark: base.red200,
  bodyNeutralLight: base.gray700,
  bodyNeutralDark: base.gray100,
  bodyPositiveLight: base.green800,
  bodyPositiveDark: base.green200,
};

const accent = {
  accentAlternativeLight: base.blue500,
  accentAlternativeDark: base.blue400,
  accentAttentionLight: base.amber500,
  accentAttentionDark: base.amber400,
  accentBrandLight: base.brand500,
  accentBrandDark: base.brand400,
  accentNegativeLight: base.red500,
  accentNegativeDark: base.red400,
  accentNeutralLight: base.gray500,
  accentNeutralDark: base.gray300,
  accentPositiveLight: base.green600,
  accentPositiveDark: base.green400,
};

const icon = {
  iconAlternativeLight: base.blue600,
  iconAlternativeDark: base.blue300,
  iconAttentionLight: base.amber600,
  iconAttentionDark: base.amber300,
  iconBrandLight: base.brand600,
  iconBrandDark: base.brand300,
  iconNegativeLight: base.red600,
  iconNegativeDark: base.red300,
  iconNeutralLight: base.gray600,
  iconNeutralDark: base.gray200,
  iconPositiveLight: base.green700,
  iconPositiveDark: base.green300,
};

const link = {
  linkAlternativeInitialLight: base.blue800,
  linkAlternativeInitialDark: base.blue200,
  linkAlternativeInteractiveLight: base.blue900,
  linkAlternativeInteractiveDark: base.blue100,
  linkAlternativeIconInitialLight: base.blue700,
  linkAlternativeIconInitialDark: base.blue300,
  linkAlternativeIconInteractiveLight: base.blue800,
  linkAlternativeIconInteractiveDark: base.blue200,

  linkAttentionInitialLight: base.amber800,
  linkAttentionInitialDark: base.amber200,
  linkAttentionInteractiveLight: base.amber900,
  linkAttentionInteractiveDark: base.amber100,
  linkAttentionIconInitialLight: base.amber700,
  linkAttentionIconInitialDark: base.amber300,
  linkAttentionIconInteractiveLight: base.amber800,
  linkAttentionIconInteractiveDark: base.amber200,

  linkBrandInitialLight: base.brand800,
  linkBrandInitialDark: base.brand200,
  linkBrandInteractiveLight: base.brand900,
  linkBrandInteractiveDark: base.brand100,
  linkBrandIconInitialLight: base.brand700,
  linkBrandIconInitialDark: base.brand300,
  linkBrandIconInteractiveLight: base.brand800,
  linkBrandIconInteractiveDark: base.brand200,

  linkNegativeInitialLight: base.red800,
  linkNegativeInitialDark: base.red200,
  linkNegativeInteractiveLight: base.red900,
  linkNegativeInteractiveDark: base.red100,
  linkNegativeIconInitialLight: base.red700,
  linkNegativeIconInitialDark: base.red300,
  linkNegativeIconInteractiveLight: base.red800,
  linkNegativeIconInteractiveDark: base.red200,

  linkNeutralInitialLight: base.gray800,
  linkNeutralInitialDark: base.gray200,
  linkNeutralInteractiveLight: base.gray900,
  linkNeutralInteractiveDark: base.gray100,
  linkNeutralIconInitialLight: base.gray700,
  linkNeutralIconInitialDark: base.gray300,
  linkNeutralIconInteractiveLight: base.gray800,
  linkNeutralIconInteractiveDark: base.gray200,

  linkPositiveInitialLight: base.green800,
  linkPositiveInitialDark: base.green200,
  linkPositiveInteractiveLight: base.green900,
  linkPositiveInteractiveDark: base.green100,
  linkPositiveIconInitialLight: base.green700,
  linkPositiveIconInitialDark: base.green300,
  linkPositiveIconInteractiveLight: base.green800,
  linkPositiveIconInteractiveDark: base.green200,
};

const disabled = {
  disabledAlternativeLight: base.blue400,
  disabledAlternativeDark: base.blue400,
  disabledAttentionLight: base.amber400,
  disabledAttentionDark: base.amber400,
  disabledBrandLight: base.brand400,
  disabledBrandDark: base.brand400,
  disabledNegativeLight: base.red400,
  disabledNegativeDark: base.red400,
  disabledNeutralLight: base.gray400,
  disabledNeutralDark: base.gray400,
  disabledPositiveLight: base.green400,
  disabledPositiveDark: base.green400,
};

const hardware = {
  hardwareBgAlternativeLight: base.blue100,
  hardwareBgAlternativeDark: base.blue800,
  hardwareStrokeAlternativeLight: base.blue300,
  hardwareStrokeAlternativeDark: base.blue500,
  hardwareStampAlternativeLight: base.blue50,
  hardwareStampAlternativeDark: base.blue50,
  hardwareContentAlternativeLight: base.blue800,
  hardwareContentAlternativeDark: base.blue100,

  hardwareBgAttentionLight: base.amber100,
  hardwareBgAttentionDark: base.amber700,
  hardwareStrokeAttentionLight: base.amber400,
  hardwareStrokeAttentionDark: base.amber500,
  hardwareStampAttentionLight: base.amber50,
  hardwareStampAttentionDark: base.amber50,
  hardwareContentAttentionLight: base.amber800,
  hardwareContentAttentionDark: base.amber100,

  hardwareBgBrandLight: base.brand200,
  hardwareBgBrandDark: base.brand600,
  hardwareStrokeBrandLight: base.brand400,
  hardwareStrokeBrandDark: base.brand400,
  hardwareStampBrandLight: base.brand50,
  hardwareStampBrandDark: base.brand50,
  hardwareContentBrandLight: base.brand800,
  hardwareContentBrandDark: base.brand50,

  hardwareBgNegativeLight: base.red100,
  hardwareBgNegativeDark: base.red700,
  hardwareStrokeNegativeLight: base.red300,
  hardwareStrokeNegativeDark: base.red400,
  hardwareStampNegativeLight: base.red50,
  hardwareStampNegativeDark: base.red50,
  hardwareContentNegativeLight: base.red800,
  hardwareContentNegativeDark: base.red100,

  hardwareBgNeutralLight: base.gray100,
  hardwareBgNeutralDark: base.gray600,
  hardwareStrokeNeutralLight: base.gray200,
  hardwareStrokeNeutralDark: base.gray400,
  hardwareStampNeutralLight: base.gray900,
  hardwareStampNeutralDark: base.gray50,
  hardwareContentNeutralLight: base.gray700,
  hardwareContentNeutralDark: base.gray100,

  hardwareBgPositiveLight: base.green100,
  hardwareBgPositiveDark: base.green800,
  hardwareStrokePositiveLight: base.green500,
  hardwareStrokePositiveDark: base.green600,
  hardwareStampPositiveLight: base.green50,
  hardwareStampPositiveDark: base.green50,
  hardwareContentPositiveLight: base.green800,
  hardwareContentPositiveDark: base.green100,

  hardwareBgDisabledLight: base.gray50,
  hardwareBgDisabledDark: base.gray700,
  hardwareStrokeDisabledLight: base.gray100,
  hardwareStrokeDisabledDark: base.gray600,
  hardwareStampDisabledLight: base.gray900,
  hardwareStampDisabledDark: base.gray50,
  hardwareContentDisabledLight: base.gray400,
  hardwareContentDisabledDark: base.gray300,
};

const internal = {
  // Internal only!
  // These colors are intended to be used ONLY for representing data/controls
  // that are only visible to Meter employees. Using them elsewhere will likely
  // result in an undesirable experience for both our customers and employees!
  internalBgLight: '#FFF7ED',
  internalBgDark: '#502B00',
  internalStrokeLight: '#FDE1C0',
  internalStrokeDark: '#854700',
  internalHeadingLight: '#8F4D00',
  internalHeadingDark: '#FEE5C7',
  internalBodyLight: '#AB5B00',
  internalBodyDark: '#F7C58B',
  internalAccentLight: '#C67D29',
  internalAccentDark: '#D69750',
  internalIconLight: '#C67D29',
  internalIconDark: '#D69750',
  internalTokenBgLight: '#F1DFC7',
  internalTokenBgDark: '#9B611D',
  internalTokenStrokeLight: '#FDE1C0',
  internalTokenStrokeDark: '#B15F00',
  internalTokenLabelLight: '#8F4D00',
  internalTokenLabelDark: '#FEE5C7',
  internalTokenIconLight: '#AB5B00',
  internalTokenIconDark: '#F7C58B',
};

export const palette = {
  // Inherits
  ...base,
  ...bg,
  ...stroke,
  ...heading,
  ...body,
  ...accent,
  ...icon,
  ...link,
  ...disabled,
  ...hardware,
  ...internal,

  // Control strokes
  controlStrokeBaseLight: base.gray600,
  controlStrokeBaseDark: base.gray300,
  controlStrokeBaseErrorLight: base.red600,
  controlStrokeBaseErrorDark: base.red300,
  controlStrokeBaseFocusedLight: base.brand600,
  controlStrokeBaseFocusedDark: base.brand500,

  controlStrokeVividPrimaryLight: base.brand900,
  controlStrokeVividPrimaryDark: base.brand300,
  controlStrokeVividPrimaryFocusedLight: base.brand900,
  controlStrokeVividPrimaryFocusedDark: base.brand300,

  controlStrokeMutedPrimaryLight: base.brand100,
  controlStrokeMutedPrimaryDark: base.brand700,

  controlStrokeVividDestructiveLight: base.red900,
  controlStrokeVividDestructiveDark: base.red300,
  controlStrokeVividDestructiveFocusedLight: base.brand900,
  controlStrokeVividDestructiveFocusedDark: base.brand300,

  controlStrokeMutedDestructiveLight: base.red100,
  controlStrokeMutedDestructiveDark: base.red700,

  controlStrokeInternalLight: base.amber600,
  controlStrokeInternalDark: base.amber300,
  controlStrokeInternalErrorLight: base.red600,
  controlStrokeInternalErrorDark: base.red300,
  controlStrokeInternalFocusedLight: base.brand600,
  controlStrokeInternalFocusedDark: base.brand500,

  controlStrokeRaisedLight: '#090B14',
  controlStrokeRaisedDark: base.gray500,
  controlStrokeRaisedFocusedLight: base.brand600,
  controlStrokeRaisedFocusedDark: base.brand900,

  // Controls backgrounds
  controlBgVividPrimaryInitialLight: base.brand600,
  controlBgVividPrimaryHoveredLight: base.brand700,
  controlBgVividPrimaryFocusedLight: base.brand700,
  controlBgVividPrimaryDisabledLight: base.brand600,

  controlBgVividPrimaryInitialDark: base.brand600,
  controlBgVividPrimaryHoveredDark: base.brand500,
  controlBgVividPrimaryFocusedDark: base.brand500,
  controlBgVividPrimaryDisabledDark: base.brand600,

  controlBgMutedPrimaryLight: base.brand50,
  controlBgMutedPrimaryDark: base.brand700,

  controlBgVividDestructiveInitialLight: base.red600,
  controlBgVividDestructiveHoveredLight: base.red700,
  controlBgVividDestructiveFocusedLight: base.red700,
  controlBgVividDestructiveDisabledLight: base.red600,

  controlBgVividDestructiveInitialDark: base.red600,
  controlBgVividDestructiveHoveredDark: base.red500,
  controlBgVividDestructiveFocusedDark: base.red500,
  controlBgVividDestructiveDisabledDark: base.red600,

  controlBgMutedDestructiveLight: base.red50,
  controlBgMutedDestructiveDark: base.red700,

  // Controls content
  controlContentAccentLight: base.gray600,
  controlContentAccentDark: base.gray100,
  controlContentPlaceholderLight: base.gray500,
  controlContentPlaceholderDark: base.gray300,
  controlContentValueLight: base.gray700,
  controlContentValueDark: base.gray50,
  controlContentInvalidLight: base.red700,
  controlContentInvalidDark: base.red100,

  // Checkered backgrounds
  checkeredAlternativeLight: bg.bgAlternativeLight,
  checkeredAlternativeDark: bg.bgAlternativeDark,
  checkeredAttentionLight: bg.bgAttentionDark,
  checkeredAttentionDark: bg.bgAttentionLight,
  checkeredBrandLight: bg.bgBrandLight,
  checkeredBrandDark: bg.bgBrandDark,
  checkeredNegativeLight: bg.bgNegativeLight,
  checkeredNegativeDark: bg.bgNegativeDark,
  checkeredNeutralLight: bg.bgNeutralLight,
  checkeredNeutralDark: bg.bgNeutralDark,
  checkeredPositiveLight: bg.bgPositiveLight,
  checkeredPositiveDark: bg.bgPositiveDark,

  // Token backgrounds
  tokenBgAlternativeLight: base.blue100,
  tokenBgAlternativeDark: base.blue600,
  tokenBgAttentionLight: base.amber100,
  tokenBgAttentionDark: base.amber600,
  tokenBgBrandLight: base.brand100,
  tokenBgBrandDark: base.brand600,
  tokenBgNegativeLight: base.red100,
  tokenBgNegativeDark: base.red600,
  tokenBgNeutralLight: base.gray100,
  tokenBgNeutralDark: base.gray500,
  tokenBgPositiveLight: base.green100,
  tokenBgPositiveDark: base.green800,

  // Token strokes
  tokenStrokeAlternativeLight: base.blue200,
  tokenStrokeAlternativeDark: base.blue500,
  tokenStrokeAttentionLight: base.amber200,
  tokenStrokeAttentionDark: base.amber500,
  tokenStrokeBrandLight: base.brand200,
  tokenStrokeBrandDark: base.brand500,
  tokenStrokeNegativeLight: base.red200,
  tokenStrokeNegativeDark: base.red500,
  tokenStrokeNeutralLight: base.gray200,
  tokenStrokeNeutralDark: base.gray400,
  tokenStrokePositiveLight: base.green300,
  tokenStrokePositiveDark: base.green600,

  // Token labels
  tokenLabelAlternativeLight: base.blue800,
  tokenLabelAlternativeDark: base.blue50,
  tokenLabelAttentionLight: base.amber800,
  tokenLabelAttentionDark: base.amber50,
  tokenLabelBrandLight: base.brand800,
  tokenLabelBrandDark: base.brand50,
  tokenLabelNegativeLight: base.red800,
  tokenLabelNegativeDark: base.red50,
  tokenLabelNeutralLight: base.gray800,
  tokenLabelNeutralDark: base.gray50,
  tokenLabelPositiveLight: base.green800,
  tokenLabelPositiveDark: base.green50,

  // Token icons
  tokenIconAlternativeLight: base.blue700,
  tokenIconAlternativeDark: base.blue100,
  tokenIconAttentionLight: base.amber700,
  tokenIconAttentionDark: base.amber100,
  tokenIconBrandLight: base.brand700,
  tokenIconBrandDark: base.brand100,
  tokenIconNegativeLight: base.red700,
  tokenIconNegativeDark: base.red100,
  tokenIconNeutralLight: base.gray700,
  tokenIconNeutralDark: base.gray100,
  tokenIconPositiveLight: base.green700,
  tokenIconPositiveDark: base.green100,

  // Chart fills
  chartFillAlternativeLight: base.blue500,
  chartFillAlternativeDark: base.blue500,
  chartFillAttentionLight: base.amber500,
  chartFillAttentionDark: base.amber500,
  chartFillBrandLight: base.brand500,
  chartFillBrandDark: base.brand500,
  chartFillNegativeLight: base.red500,
  chartFillNegativeDark: base.red500,
  chartFillNeutralLight: base.gray300,
  chartFillNeutralDark: base.gray400,
  chartFillPositiveLight: base.green500,
  chartFillPositiveDark: base.green500,

  // Chart strokes
  chartStrokeAlternativeLight: base.blue600,
  chartStrokeAlternativeDark: base.blue400,
  chartStrokeAttentionLight: base.amber600,
  chartStrokeAttentionDark: base.amber400,
  chartStrokeBrandLight: base.brand600,
  chartStrokeBrandDark: base.brand400,
  chartStrokeNegativeLight: base.red600,
  chartStrokeNegativeDark: base.red400,
  chartStrokeNeutralLight: base.gray400,
  chartStrokeNeutralDark: base.gray300,
  chartStrokePositiveLight: base.green600,
  chartStrokePositiveDark: base.green400,
};

export type ColorPalette = keyof typeof palette;

export { applicationThemeHex };

/**
 * Sets the opacity of the given CSS color specifier string and returns the new color formatted as an rgba() string.
 * @param specifier
 * @param opacity
 */
export const fade = (specifier: string, opacity: number): string => {
  const c = d3.color(specifier);

  if (c) {
    c.opacity = opacity;
    return c.formatRgb();
  }

  return 'rgba(0,0,0,0)';
};

/** Variant background colors for matching (light and dark mode) */
export const VARIANT_BG_COLORS: Record<VariantProp, [string, string]> = {
  alternative: [palette.bgAlternativeLight, palette.bgAlternativeDark],
  attention: [palette.bgAttentionLight, palette.bgAttentionDark],
  brand: [palette.bgBrandLight, palette.bgBrandDark],
  negative: [palette.bgNegativeLight, palette.bgNegativeDark],
  neutral: [palette.bgNeutralLight, palette.bgNeutralDark],
  positive: [palette.bgPositiveLight, palette.bgPositiveDark],
};

/** Application background (white/gray) - maps to neutral for shadow purposes */
export const APP_BG_COLORS = [palette.bgApplicationLight, palette.bgApplicationDark];

/** All known background colors for variant matching */
export const ALL_BG_COLORS: Array<{ variant: VariantProp; colors: string[] }> = [
  ...Object.entries(VARIANT_BG_COLORS).map(([variant, colors]) => ({
    variant: variant as VariantProp,
    colors: [...colors],
  })),
  { variant: 'neutral', colors: APP_BG_COLORS }, // Application uses neutral-style shadow
];
